import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, DollarSign, Globe, Clock } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

const currencies = [
  { value: "NOK", label: "NOK - Norske kroner", symbol: "kr" },
  { value: "USD", label: "USD - US Dollar", symbol: "$" },
  { value: "EUR", label: "EUR - Euro", symbol: "€" },
  { value: "GBP", label: "GBP - British Pound", symbol: "£" },
];

const languages = [
  { value: "no", label: "Norsk" },
  { value: "en", label: "English" },
];

export default function ProfileSettings({ user }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [weeklyCapacity, setWeeklyCapacity] = useState("");
  const [currency, setCurrency] = useState(user?.display_currency || "NOK");
  const [language, setLanguage] = useState(user?.language || "no");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user) {
      // Use stored first/last name if available, otherwise split full_name
      if (user.first_name || user.last_name) {
        setFirstName(user.first_name || "");
        setLastName(user.last_name || "");
      } else {
        const nameParts = user.full_name?.split(" ") || [];
        setFirstName(nameParts[0] || "");
        setLastName(nameParts.slice(1).join(" ") || "");
      }
      setHourlyRate(user.hourly_rate || "");
      setWeeklyCapacity(user.weekly_capacity || 37.5);
    }
  }, [user]);

  const updateSettingsMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success("Innstillinger oppdatert");
    },
  });

  const handleSave = () => {
    updateSettingsMutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      hourly_rate: parseFloat(hourlyRate) || 0,
      weekly_capacity: parseFloat(weeklyCapacity) || 37.5,
      display_currency: currency,
      language: language,
    });
  };

  return (
    <div className="space-y-6">
      {/* Profile Info */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <User className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Profilinformasjon</h3>
            <p className="text-sm text-slate-500">Dine brukerdetaljer</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">Fornavn</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Skriv inn fornavn"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Etternavn</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Skriv inn etternavn"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-post</Label>
            <Input
              id="email"
              value={user?.email}
              disabled
              className="h-11 bg-slate-50"
            />
            <p className="text-xs text-slate-500">E-post kan ikke endres</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hourlyRate">Timepris (NOK)</Label>
            <Input
              id="hourlyRate"
              type="number"
              min="0"
              step="100"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="f.eks. 1500"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="weeklyCapacity">Ukentlig kapasitet (timer)</Label>
            <Input
              id="weeklyCapacity"
              type="number"
              min="0"
              max="168"
              step="0.5"
              value={weeklyCapacity}
              onChange={(e) => setWeeklyCapacity(e.target.value)}
              placeholder="f.eks. 37.5"
              className="h-11"
            />
          </div>
        </div>
      </Card>

      {/* Currency & Language Settings */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Visningsinnstillinger</h3>
            <p className="text-sm text-slate-500">Valuta og språkpreferanser</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currency">Valuta for finansielle data</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger id="currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.value} value={curr.value}>
                    {curr.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Alle beløp vil vises i valgt valuta på dashbordet og i rapporter
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="language">Språk</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {lang.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Velg foretrukket språk for grensesnittet
            </p>
          </div>

          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave}
              disabled={updateSettingsMutation.isPending}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {updateSettingsMutation.isPending ? "Lagrer..." : "Lagre endringer"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}